import { Observable } from 'rxjs/Observable';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { of } from 'rxjs/observable/of';
import { Delegation } from '../models/delegation';
import { Group } from '../models/group';
import { Role } from '../models/role';
import { Team, PaginatedTeamDTO } from '../models/team';
import { Router } from '@angular/router';
import { PaginatedAuthEntityDTO } from '../models/auth-entity';
import { RoleRequest, RoleResponse } from '../models/role-assignment-map';

@Injectable()
export class AuthService {

  baseUrl = environment.authServiceBaseUrl;

  constructor(private http: HttpClient,
    private router: Router
  ) { }

  login(username: string, password: string): Observable<boolean> {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const formData: FormData = new FormData();
    formData.append('grant_type', 'password');
    formData.append('username', username);
    formData.append('password', password);
    formData.append('scope', 'write');
    this.removeTheme();
    return this.http.post<any>(`${this.baseUrl}oauth/token`, formData, { headers: headers })
      .map(oauth => {
        this.storeToken(oauth);
        return true;
      });
  }

  // Oauth response example
  // {
  //     "access_token":"92210e1a-c5a1-4218-8e09-a86843087688",
  //     "token_type":"bearer",
  //     "refresh_token":"f193cf84-bc15-48be-8f14-249527433b8e",
  //     "expires_in":14400,
  //     "scope":"read write",
  //     "roles":["ADMIN"]
  //   }

  logout() {
    this.removeToken();
    this.removeUserInfo();
    this.removeTheme();
    localStorage.removeItem('marketAccountNo');
    localStorage.removeItem('marketLocationCode');
    localStorage.removeItem('marketType');
    this.router.navigate(['login']);
  }

  isUsernameInUsed(username) {
    const url = `${this.baseUrl}auth/userexists/${username}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<boolean>(url, { headers })
      .catch(err => {
        if (err.status === 404) {
          return of(false);
        } else {
          throw err;
        }
      });
  }

  isAuthenticated(): boolean {
    if (this.getOauthExpiration() < new Date().getTime()) {
      return false;
    }
    return true;
  }

  getRoles() {
    return ['ADMIN', 'LIST_CLIENTS', 'ORGANIZATION_CREATE', 'EDIT_CLIENT',
      'DELETE_CLIENT', 'LIST_USERS', 'USER_CREATE', 'EDIT_USER', 'DELETE_USER',
      'LIST_MARKETS', 'EDIT_MARKET', 'DELETE_MARKET', 'CREATE_MARKET', 'UPLOAD_REPORTS',
      'DOWNLOAD_REPORTS', 'CREATE_NEW_PROJECT', 'EDIT_PROJECT', 'DELETE_WORKSHEET',
      'UPLOAD_NEW_MASTER_BOM', 'CREATE_COMMENT', 'ASSIGN_DELEGATES', 'START_NEW_WORKSHEET',
      'EDIT_MASTER_BOM', 'EDIT_WORKSHEET', 'VIEW_PROBLEM_WITH_ORDER_COMMENT',
      'VIEW_WAREHOUSE_COMMENT', 'CREATE_WAREHOUSE_COMMENT', 'CREATE_PROBLEM_WITH_ORDER_COMMENT'];
    // return this.getOauth().roles;
  }

  /* refreshPermissions() {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<{ 'Role Assignments': { 'Role': string }[] }>(`${this.baseUrl}/roles/users/me`, { headers: headers })
      .do(res => {
        const oauth = this.getOauth();
        oauth.roles = res['Role Assignments'].map(r => r.Role);
        localStorage.setItem('oauth', JSON.stringify(oauth));
      });
  } */

  getAccessToken(): string {
    return this.getOauth().access_token;
  }

  getUserId(): string {
    return this.getOauth().user_id;
  }

  checkToken() {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}oauth/check_token`;
    const params = new HttpParams()
      .set('token', this.getAccessToken());
    return this.http.get<any>(url, { params, headers });
  }

  getTheme() {
    return localStorage.getItem('theme');
  }

  storeTheme(theme: string) {
    localStorage.setItem('theme', theme);
  }

  private removeTheme() {
    localStorage.removeItem('theme');
  }

  private storeToken(oauth) {
    const expiresAt = new Date().getTime() + oauth.expires_in * 1000;
    localStorage.setItem('oauth', JSON.stringify(oauth));
    localStorage.setItem('oauth-expiration', expiresAt.toString());
  }

  private removeToken() {
    localStorage.removeItem('oauth');
    localStorage.removeItem('oauth-expiration');
  }

  private removeUserInfo() {
    localStorage.removeItem('currentUserMarketIds');
  }

  private getOauth() {
    try {
      return JSON.parse(localStorage.getItem('oauth'));
    } catch (ex) {
      return {};
    }
  }

  private getOauthExpiration(): number {
    try {
      const expiration = parseInt(localStorage.getItem('oauth-expiration'), 10);
      if (expiration) {
        return expiration;
      }
      return null;
    } catch (ex) {
      return null;
    }
  }

  resetPassword(username: string) {
    const url = `${this.baseUrl}auth/resetPassword/${username}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<void>(url, { headers });
  }

  createPassword(password: string, token: string) {
    const url = `${this.baseUrl}auth/changePassword`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const body = { password: password, token: token };
    return this.http.post<void>(url, body, { headers });
  }

  changeUserPassword(userId: string, oldPassword: string, newPassword: string) {
    const url = `${this.baseUrl}auth/changeUserPassword`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const body = { userId: userId, oldPassword: oldPassword, newPassword: newPassword };
    return this.http.post<boolean>(url, body, { headers });
  }

  /**
   * DELEGATIONS
   */
  getDelegations() {
    const url = `${this.baseUrl}delegations`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Delegation[]>(url, { headers });
  }

  getDelegationByDelegationId(delegationId: string) {
    const url = `${this.baseUrl}delegations/${delegationId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Delegation>(url, { headers });
  }

  createDelegation(delegation: Delegation) {
    const url = `${this.baseUrl}delegations`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<Delegation>(url, delegation, { headers });
  }
  /**
   * END DELEGATIONS
   */

  findGroups() {
    const url = `${this.baseUrl}groups`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Group[]>(url, { headers });
  }

  getGroupUsers(groupId: string) {
    const url = `${this.baseUrl}groups/${groupId}/users`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<string[]>(url, { headers });
  }

  updateGroup(group: Group) {
    const url = `${this.baseUrl}groups/${group.Id}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.put<void>(url, group, { headers });
  }

  getUserNameByUserId(userId: string) {
    const url = `${this.baseUrl}auth/userNameByUserId/${userId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get(url, { headers, responseType: 'text' });
  }

  getUserName() {
    const url = `${this.baseUrl}auth/getUserName`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get(url, { headers, responseType: 'text' });
  }

  addUsersToGroup(teamId: string, userId: string) {
    const url = `${this.baseUrl}groups/${teamId}/users`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const body = { 'User IDs': [userId] };
    return this.http.post<void>(url, body, { headers });
  }

  removeUsersFromGroup(teamId: string, userId: string) {
    const url = `${this.baseUrl}groups/${teamId}/remove-users`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const body = { 'User IDs': [userId] };
    return this.http.post<void>(url, body, { headers });
  }

  /**
   * Roles Section
   */
  
   checkSpecificRoleForUser(userId: string, roleName: string) {
    const url = `${this.baseUrl}roles/users/${userId}/${roleName}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
     return this.http.get(url, { headers, responseType: 'text' });
  }

  getUserRolesInContextByUser(userId: string, context: string) {
    const url = `${this.baseUrl}roles/users/${userId}/contexts/${context}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<any>(url, { headers }).map(response => response.roles);
  }

  getUserRolesInContext(context: string, includeHierarchy: boolean = true) {
    const url = `${this.baseUrl}roles/roles/${context}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('include_hierarchy', includeHierarchy.toString());
    return this.http.get<any>(url, { params, headers }).map(response => response.roles);
  }

  getUserRoles(roleRequest: RoleRequest) {
    const url = `${this.baseUrl}roles/users/me`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<RoleResponse>(url, roleRequest, { headers }).map(response => {
      var roleResponse = new RoleResponse();
      roleResponse.roleMap = roleResponse.mapResponse(response);
      return roleResponse;
    });
  }

  /**
   * Team Section
   */
  getTeamsForOrganization(orgId: string, userId?: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}teams`;
    let params = new HttpParams()
      .set('organizationId', orgId)
      .set('page', '0')
      .set('size', '1000')
      .set('sort', 'name,asc');
      if(userId) {
        params = params.set('userId', userId);
      }
    return this.http.get<Team[]>(url, { params, headers });
  }

  getTeamsWithPermission(teamList: any){
    const url = `${this.baseUrl}teams/byTeamIds`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const params = new HttpParams().set('teamListString', teamList);
    return this.http.get<Team[]>(url, { params, headers });
  }

  getTeamsByMarketId(marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}teams/market`;
    const params = new HttpParams()
      .set('marketId', marketId)
      .set('page', '0')
      .set('size', '1000')
      .set('sort', 'name,asc');
    return this.http.get<Team[]>(url, { params, headers });
  }

  findTeamsByOrganizationIdAndText(orgId: string, text: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}teams/byOrgIdAndText`;
    const params = new HttpParams()
      .set('organizationID', orgId)
      .set('text', text)
      .set('page', '0')
      .set('size', '1000')
      .set('sort', 'name,asc');
    return this.http.get<PaginatedTeamDTO>(url, { params, headers })
      .map(response => response.records);

  }

  findTeamsAndUsersByOrganizationIdAndText(orgId: string, text: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}auth-entities/byOrgIdAndText`;
    const params = new HttpParams()
      .set('organizationID', orgId)
      .set('text', text)
      .set('page', '0')
      .set('size', '1000')
      .set('sort', 'name,asc');
    return this.http.get<PaginatedAuthEntityDTO>(url, { params, headers })
      .map(response => response.records);

  }

  getTeamById(teamId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}teams/${teamId}`;
    return this.http.get<Team>(url, { headers });
  }

  createTeam(team: Team) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}teams`;
    return this.http.post<void>(url, team, { headers });
  }

  updateTeam(team: Team) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}teams/${team.id}`;
    return this.http.put<void>(url, team, { headers });
  }

  deleteTeam(teamId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}teams/${teamId}`;
    return this.http.delete<void>(url, { headers });
  }

  getUserTeamByType(id: string, teamType: number) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('teamType', teamType.toString());
    const url = `${this.baseUrl}groups/userTeamByType/${id}`;
    return this.http.get<Team>(url, { params, headers });
  }


  isUserAnAdmin(userId: string){

    const url = `${this.baseUrl}auth/isadmin/${userId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
     return this.http.get(url, { headers, responseType: 'text' });
  }
  /**
   * End Team Section
   */

   getActiveUsersOfGroups(ids: string[]) {
    const headers = new HttpHeaders()
    .set('Accept', 'application/json');
    const url = `${this.baseUrl}groups/activeUsersByGroupIds`;
    return this.http.post<string[]>(url, ids, { headers });
  }

  getOrganizationTeams(orgId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}teams/getOrganizationTeams`;
    let params = new HttpParams().set('organizationId', orgId);
    return this.http.get<Team[]>(url, { params, headers });
  }

  getTeamsByTeamIds(teamIds: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}teams/byTeamIds`;
    let params = new HttpParams().set('teamListString', teamIds);
    return this.http.get<Team[]>(url, { params, headers });
  }

}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/api/services/auth.service.ts